import { ButtonPressedTriggerPayload, TimeBasedTriggerPayload, TriggerCreate, TriggerType } from '@ekkogmbh/apisdk';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CenteredColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';

const validateMoment = (date: Moment | null): Moment | null => {
  if (date == null || !date.isValid()) {
    enqueueSnackbar('Invalid date.', { variant: 'error' });
    return null;
  }

  if (date.isBefore(moment())) {
    return moment();
  }

  return date;
};

interface Props {
  triggers: TriggerCreate[];
  onChange: (tiggers: TriggerCreate[]) => void;
}

export const TriggerForm = ({ triggers, onChange }: Props): React.JSX.Element => {
  const { enableNext } = usePanel();

  useEffect(() => {
    enableNext(triggers.length > 0);
  }, [triggers]);

  const setTriggerValue = useCallback(
    (index: number, key: string, value: string) => {
      const tc = triggers[index];
      triggers[index] = {
        ...tc,
        payload: {
          ...tc.payload,
          [key]: value,
        },
      };
      onChange([...triggers]);
    },
    [triggers, onChange],
  );

  const setTriggerTimeValue = useCallback(
    (index: number, key: 'validFrom' | 'validUntil' | 'datetime', date: Moment | null) => {
      date = validateMoment(date);
      if (date === null) {
        return;
      }
      const payload = triggers[index].payload as ButtonPressedTriggerPayload;
      const validUntil = key === 'validUntil' ? date : payload.validUntil ? moment(payload.validUntil) : null;
      const validFrom = key === 'validFrom' ? date : payload.validFrom ? moment(payload.validFrom) : null;

      if (validFrom && validUntil && !validFrom.isBefore(validUntil)) {
        enqueueSnackbar('Valid-From date must be before Valid-Until date.', { variant: 'error' });

        return;
      }

      setTriggerValue(index, key, date.set('seconds', 0).format());
    },
    [setTriggerValue, triggers],
  );

  const setTriggerDateTimeValue = useCallback(
    (index: number, value: Moment | null) => {
      const date = validateMoment(value);
      if (date === null) {
        return;
      }

      setTriggerValue(index, 'datetime', date.set('seconds', 0).format());
    },
    [setTriggerValue],
  );

  const renderTrigger = useCallback(
    (triggerCreate: TriggerCreate, index: number) => {
      let form: React.JSX.Element;
      switch (triggerCreate.type) {
        case TriggerType.BUTTON_PRESSED: {
          const payload = triggerCreate.payload as ButtonPressedTriggerPayload;
          form = (
            <CenteredColumn width={'100%'}>
              <StyledTextField
                label={'Label-Id'}
                value={payload.labelId}
                onChange={(e) => setTriggerValue(index, 'labelId', String(e.target.value))}
              />
              <StyledTextField
                label="Technology"
                value={payload.technology}
                onChange={(e) => setTriggerValue(index, 'technology', String(e.target.value))}
              />
              <StyledTextField
                label="Button-Index"
                value={payload.buttonIndex}
                onChange={(e) => setTriggerValue(index, 'buttonIndex', String(e.target.value))}
              />
              <ReactiveRow>
                <DateTimePicker
                  sx={{ width: '100%' }}
                  disabled={payload.validFrom === null}
                  label="Valid-From"
                  ampm={false}
                  disablePast
                  value={
                    payload.validFrom && payload.validFrom !== '' ? moment(payload.validFrom) : moment().add(1, 'day')
                  }
                  onChange={(m) => setTriggerTimeValue(index, 'validFrom', m)}
                />
                <DateTimePicker
                  sx={{ width: '100%' }}
                  disabled={payload.validUntil === null}
                  label="Valid-Until"
                  ampm={false}
                  disablePast
                  value={
                    payload.validUntil && payload.validUntil !== ''
                      ? moment(payload.validUntil)
                      : moment().add(1, 'day')
                  }
                  onChange={(m) => setTriggerTimeValue(index, 'validUntil', m)}
                />
              </ReactiveRow>
            </CenteredColumn>
          );
          break;
        }
        case TriggerType.TIME_BASED: {
          const payload = triggerCreate.payload as TimeBasedTriggerPayload;
          form = (
            <DateTimePicker
              sx={{ width: '100%' }}
              label="Trigger-At"
              ampm={false}
              disablePast
              value={payload.datetime !== '' ? moment(payload.datetime) : moment()}
              onChange={(m) => setTriggerDateTimeValue(index, m)}
            />
          );
          break;
        }
        default:
          form = <></>;
      }
      return (
        <CenteredColumn key={index} width={'72%'} spacing={2}>
          <StyledSelectField
            native
            label="Type"
            value={triggerCreate.type}
            onChange={(e) => {
              triggerCreate.type = e.target.value as TriggerType;
              triggers[index] = { ...triggerCreate };
              onChange([...triggers]);
            }}
          >
            <option key={0} value={TriggerType.TIME_BASED}>
              Time-Based
            </option>
            <option key={1} value={TriggerType.BUTTON_PRESSED}>
              Button-Pressed
            </option>
          </StyledSelectField>
          {form}
        </CenteredColumn>
      );
    },
    [triggers],
  );

  return (
    <CenteredColumn width={'100%'}>
      {triggers.map((t: TriggerCreate, index: number) => renderTrigger(t, index))}
    </CenteredColumn>
  );
};
